import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { Badge } from '../lib/badge';
import Icon from '../components/icons';
import { EmptyState } from './ListView';
import { canOperate, currentRole } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet('/approvals')
      .then(setRows)
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  async function decide(id, decision) {
    setBusyId(id);
    setMsg('');
    try {
      await apiPost(`/approvals/${id}/decide`, {
        decision,
        decided_by: `mvp-${currentRole()}`,
        reason: decision === 'approved' ? 'MVP approval granted' : 'MVP approval rejected',
      });
      setMsg(`Approval #${id} ${decision}.`);
      load();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="page">
      <div className="page__header">
        <span className="page__icon"><Icon name="approvals" /></span>
        <div className="page__header-text">
          <h2>Approval Queue</h2>
          <p>High-risk / gated skill runs wait here. Approve to enqueue Claude; reject to fail the task.</p>
        </div>
      </div>
      <RoleBanner action="approve or reject runs" />
      {err && <div className="status status--error">{err}</div>}
      {msg && <div className="status status--muted">{msg}</div>}
      {loading ? (
        <p className="status status--muted">Loading approvals…</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Task</th>
                <th>Skill</th>
                <th>Run</th>
                <th>Status</th>
                <th>SLA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="cell-strong">#{r.id}</td>
                  <td>
                    {r.task_id != null ? (
                      <span title={r.task_title || ''}>
                        #{r.task_id}{r.task_title ? ` · ${r.task_title}` : ''}
                      </span>
                    ) : '—'}
                  </td>
                  <td>{r.skill_name || '—'}</td>
                  <td className="cell-muted">
                    {r.orchestration_run_id
                      ? `orch #${r.orchestration_run_id}`
                      : r.run_id
                        ? `legacy #${r.run_id}`
                        : '—'}
                  </td>
                  <td><Badge value={r.status} /></td>
                  <td className="cell-muted">{r.sla_deadline ? new Date(r.sla_deadline).toLocaleString() : '—'}</td>
                  <td className="actions-cell">
                    {r.status === 'pending' ? (
                      canOperate() ? (
                        <>
                          <button
                            type="button"
                            className="btn-primary btn-small"
                            disabled={busyId === r.id}
                            onClick={() => decide(r.id, 'approved')}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn-danger btn-small"
                            disabled={busyId === r.id}
                            onClick={() => decide(r.id, 'rejected')}
                          >
                            Reject
                          </button>
                        </>
                      ) : (
                        <span className="cell-muted">View only</span>
                      )
                    ) : (
                      <span className="cell-muted">
                        {r.decided_by ? `Decided by ${r.decided_by}` : 'Decided'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <EmptyState label="No approval gates." />}
        </div>
      )}
    </main>
  );
}
